<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/shared/ui/dropdown-menu";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { pickTagVariant } from "@/shared/ui/tag";

const { t } = useI18n();

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
  },
  participantTypes: {
    type: Array,
    default: () => [],
  },
  participants: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["select"]);

const openIndividual = ref(false);
const openLegal = ref(false);

const VARIANT_TO_DOT = {
  primary: "bg-blue-500",
  success: "bg-green-500",
  danger: "bg-red-500",
  warning: "bg-yellow-500",
  rose: "bg-rose-500",
  violet: "bg-violet-500",
  teal: "bg-teal-500",
  orange: "bg-orange-500",
};

function buildRoles(allowedCodes) {
  return props.participantTypes
    .filter((l) => (allowedCodes || []).includes(l.code))
    .map((role) => ({
      code: role.code,
      name: role.name,
      raw: role,
      dotClass:
        VARIANT_TO_DOT[pickTagVariant(role.name)] || "bg-muted-foreground",
      disabled: props.participants.some(
        (p) => p.participant_type_code === role.code
      ),
    }));
}

const individualRoles = computed(() =>
  buildRoles(props.template?.participant_fl_type_codes)
);
const legalRoles = computed(() =>
  buildRoles(props.template?.participant_ul_type_codes)
);

const individualDisabled = computed(() => individualRoles.value.length === 0);
const legalDisabled = computed(() => legalRoles.value.length === 0);

function selectRole(type, role) {
  if (role.disabled) return;
  emit("select", { type, role: role.raw });
}

function reset() {
  openIndividual.value = false;
  openLegal.value = false;
}

defineExpose({ reset });
</script>

<template>
  <div class="w-full flex flex-col gap-3">
    <div class="flex items-center gap-2">
      <div class="text-foreground text-lg font-semibold">
        {{ t("Участники") }}
      </div>
      <div
        v-if="participants?.length"
        class="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-muted text-muted-foreground text-xs font-medium"
      >
        {{ participants.length }}
      </div>
    </div>

    <div
      :class="[
        'gap-3',
        participants?.length ? 'flex flex-col' : 'grid grid-cols-2',
      ]"
    >
      <DropdownMenu v-model:open="openIndividual">
        <DropdownMenuTrigger as-child :disabled="individualDisabled">
          <div
            role="button"
            :tabindex="individualDisabled ? -1 : 0"
            :aria-disabled="individualDisabled"
            :class="[
              'group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-card transition select-none',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              individualDisabled
                ? 'opacity-50 cursor-not-allowed pointer-events-none'
                : 'cursor-pointer hover:border-primary/40',
              openIndividual
                ? 'border-primary ring-1 ring-primary/20'
                : 'border-border',
            ]"
          >
            <div
              class="flex items-center justify-center w-6 h-6 bg-blue-50 text-blue-600 shrink-0"
              style="border-radius: var(--radius)"
            >
              <SvgIcon name="huge-user" class="w-3.5 h-3.5" />
            </div>
            <div class="flex-1 text-left text-xs font-medium text-foreground">
              {{ t("Физ лицо") }}
            </div>
            <SvgIcon
              name="huge-shevron-down"
              :class="[
                'w-4 h-4 text-muted-foreground transition-transform',
                openIndividual ? 'rotate-180' : '',
              ]"
            />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          :side-offset="6"
          class="w-[--reka-dropdown-menu-trigger-width] p-2"
        >
          <DropdownMenuLabel
            class="px-2 pt-1 pb-2 text-[12px] font-medium text-muted-foreground"
          >
            {{ t("Физ лицо") }} · {{ t("Выбрать роль") }}
          </DropdownMenuLabel>

          <DropdownMenuItem
            v-for="role in individualRoles"
            :key="role.code"
            :disabled="role.disabled"
            class="gap-3 px-2 py-2 cursor-pointer"
            @select="selectRole('INDIVIDUAL', role)"
          >
            <div :class="['w-2 h-2 rounded-full shrink-0', role.dotClass]" />
            <div class="flex-1 text-sm text-foreground">{{ role.name }}</div>
            <div
              v-if="role.disabled"
              class="flex items-center justify-center w-4 h-4 rounded-full bg-foreground text-background shrink-0"
            >
              <SvgIcon name="huge-check" class="w-2.5 h-2.5" />
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu v-model:open="openLegal">
        <DropdownMenuTrigger as-child :disabled="legalDisabled">
          <div
            role="button"
            :tabindex="legalDisabled ? -1 : 0"
            :aria-disabled="legalDisabled"
            :class="[
              'group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-card transition select-none',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              legalDisabled
                ? 'opacity-50 cursor-not-allowed pointer-events-none'
                : 'cursor-pointer hover:border-primary/40',
              openLegal
                ? 'border-primary ring-1 ring-primary/20'
                : 'border-border',
            ]"
          >
            <div
              class="flex items-center justify-center w-6 h-6 bg-violet-50 text-violet-600 shrink-0"
              style="border-radius: var(--radius)"
            >
              <SvgIcon name="huge-organization" class="w-3.5 h-3.5" />
            </div>
            <div class="flex-1 text-left text-xs font-medium text-foreground">
              {{ t("Юр лицо") }}
            </div>
            <SvgIcon
              name="huge-shevron-down"
              :class="[
                'w-4 h-4 text-muted-foreground transition-transform',
                openLegal ? 'rotate-180' : '',
              ]"
            />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          :side-offset="6"
          class="w-[--reka-dropdown-menu-trigger-width] p-2"
        >
          <DropdownMenuLabel
            class="px-2 pt-1 pb-2 text-[12px] font-medium text-muted-foreground"
          >
            {{ t("Юр лицо") }} · {{ t("Выбрать роль") }}
          </DropdownMenuLabel>

          <DropdownMenuItem
            v-for="role in legalRoles"
            :key="role.code"
            :disabled="role.disabled"
            class="gap-3 px-2 py-2 cursor-pointer"
            @select="selectRole('LEGAL', role)"
          >
            <div :class="['w-2 h-2 rounded-full shrink-0', role.dotClass]" />
            <div class="flex-1 text-sm text-foreground">{{ role.name }}</div>
            <div
              v-if="role.disabled"
              class="flex items-center justify-center w-4 h-4 rounded-full bg-foreground text-background shrink-0"
            >
              <SvgIcon name="huge-check" class="w-2.5 h-2.5" />
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
